// src/pages/UnlabelledTransferCreateDualBucket.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiBox,
  FiSend,
  FiSearch,
  FiRefreshCcw,
  FiArrowRight,
  FiArrowLeft,
  FiFilter,
  FiX,
} from "react-icons/fi";
import { FaArrowRightArrowLeft } from "react-icons/fa6";

import {
  useCreateUnlabelledTransferMutation,
  useGetAllUnlabelledBucketsQuery,
} from "../toolkitServices/unlabelledApi";

import { useGetAllEmployeesAllQuery } from "../toolkitServices/employeeApi";
import {
  useGetCountersQuery,
  useGetCountersAllQuery,
} from "../toolkitServices/counterApi";
import {
  useGetAllBranchesQuery,
  useGetAllBranchesAllQuery,
} from "../toolkitServices/branchApi";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

// debounce
const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// media query
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, [query]);

  return matches;
};

/* ------------ react-select styles ------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 14,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px #7c3aed1f" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.85rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 38 }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "0.85rem",
  }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#0f172a",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// table classes
const thCls =
  "px-3 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap";
const tdCls = "px-3 py-2 text-[11px] sm:text-xs align-middle";
const chipCls =
  "inline-flex items-center gap-1 rounded-full bg-white/12 px-2 py-1 text-[10px]";

// same left height as labelled page
const tableScrollCls =
  "min-h-0 overflow-auto h-[340px] sm:h-[380px] md:h-[168px]";

/* -------------------- helpers -------------------- */
const n = (v) => (Number.isFinite(+v) ? +v : 0);
const r3 = (v) => Number(n(v).toFixed(3));
const k = (v) => String(v ?? "");
const sameId = (a, b) => k(a) === k(b);

const comboTitle = (b) => {
  const cat = b?.categoryName || "—";
  const prod = b?.productName || "—";
  const des = b?.designName || "—";
  const pur = b?.purityName || "—";
  return `${cat} • ${prod} • ${des} • ${pur}`;
};

const balance = (b) => ({
  qty: n(b?.balance?.qty),
  netWt: r3(b?.balance?.netWt),
  grossWt: r3(b?.balance?.grossWt),
  totalWt: r3(b?.balance?.totalWt),
  stoneWt: r3(b?.balance?.stoneWt),
  diamondWt: r3(b?.balance?.diamondWt),
});

const withDefaultTransfer = (b) => {
  const bal = balance(b);

  const grossWt = String(bal.grossWt || "");
  const stoneWt = String(bal.stoneWt || "");
  const diamondWt = String(bal.diamondWt || "");
  const netWt = calcNetWt(grossWt, stoneWt, diamondWt);

  return {
    ...b,
    _transfer: {
      qty: String(bal.qty || ""),
      netWt,
      grossWt,
      totalWt: String(bal.totalWt || ""),
      stoneWt,
      diamondWt,
    },
  };
};

const calcNetWt = (grossWt, stoneWt, diamondWt) => {
  const gross = n(grossWt);
  const stone = n(stoneWt);
  const diamond = n(diamondWt);

  const result = gross - stone - diamond * 0.2;
  return String(r3(Math.max(0, result)));
};

const UnlabelledTransferCreateDualBucket = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);

  const tenantId = user?.tenantId;
  const limit = 100;

  const isStaff = (user?.role || "").toLowerCase() === "staff";
  const isSmUp = useMediaQuery("(min-width: 640px)");

  const [transferMode, setTransferMode] = useState("counter");

  const emp = user?.employeeDetails || null;
  const myEmployeeId = user?.employeeId || emp?._id || user?._id || user?.id || null;
  const myEmployeeName = emp
    ? `${emp.firstName || ""} ${emp.lastName || ""}`.trim() ||
      emp.officialEmail ||
      emp.email ||
      "Employee"
    : user?.userName || user?.name || "Employee";

  const allowedBranchIds = useMemo(() => {
    const arr = emp?.branches?.length ? emp.branches : emp?.branch ? [emp.branch] : [];
    return arr.map(String);
  }, [emp]);

  const allowedCounterIds = useMemo(() => {
    const arr = emp?.counters?.length ? emp.counters : emp?.counter ? [emp.counter] : [];
    return arr.map(String);
  }, [emp]);

  // form states
  const [fromBranch, setFromBranch] = useState(null);
  const [toBranch, setToBranch] = useState(null);
  const [fromCounter, setFromCounter] = useState(null);
  const [toCounter, setToCounter] = useState(null);
  const [fromEmployee, setFromEmployee] = useState(null);
  const [toEmployee, setToEmployee] = useState(null);

  // search + paging + selection
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage] = useState(1);

  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [checkedLeft, setCheckedLeft] = useState([]);
  const [checkedRight, setCheckedRight] = useState([]);

  // filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    showZero: false,
    onlyActive: true,
  });

  useEffect(() => {
    if (!showFilterModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowFilterModal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showFilterModal]);

  /* =========================
      QUERIES
  ========================= */
  const { data: branchesFromData } = useGetAllBranchesQuery(
    { page: 1, limit: 500, search: "" },
    { skip: !tenantId }
  );

  const { data: branchesAllData } = useGetAllBranchesAllQuery(
    { page: 1, limit: 1000, search: "" },
    { skip: !tenantId }
  );

  const { data: countersFromData } = useGetCountersQuery(
    { page: 1, limit: 1000, search: "" },
    { skip: !tenantId }
  );

  const { data: countersAllData } = useGetCountersAllQuery(
    { page: 1, limit: 3000, search: "" },
    { skip: !tenantId }
  );

  const shouldFetchFromEmployees = !!(!isStaff && (fromBranch?.value || fromCounter?.value));
  const shouldFetchToEmployees = !!(toBranch?.value || toCounter?.value);

  const { data: fromEmployeesResp } = useGetAllEmployeesAllQuery(
    {
      page: 1,
      limit: 500,
      search: "",
      branch: fromBranch?.value || undefined,
      counter: fromCounter?.value || undefined,
    },
    { skip: !tenantId || !shouldFetchFromEmployees }
  );

  const { data: toEmployeesResp } = useGetAllEmployeesAllQuery(
    {
      page: 1,
      limit: 500,
      search: "",
      branch: toBranch?.value || undefined,
      counter: toCounter?.value || undefined,
    },
    { skip: !tenantId || !shouldFetchToEmployees }
  );

  /* =========================
      BRANCH OPTIONS
  ========================= */
  // same as labelled transfer page
  const fromBranches = useMemo(() => {
    const list = (branchesFromData?.data || []).map((b) => ({
      label: b.branchName,
      value: String(b._id),
    }));
    return list;
  }, [branchesFromData]);

  const toBranches = useMemo(() => {
    return (branchesAllData?.data || []).map((b) => ({
      label: b.branchName,
      value: String(b._id),
    }));
  }, [branchesAllData]);

  /* =========================
      COUNTER OPTIONS
  ========================= */
  const restrictedCounters = useMemo(() => countersFromData?.data || [], [countersFromData]);
  const allTenantCounters = useMemo(() => countersAllData?.data || [], [countersAllData]);

  const getCounterBranchId = (c) => String(c?.branchId?._id ?? c?.branchId ?? "");

  // same as labelled transfer page
  const filteredFromCounters = useMemo(() => {
    if (!fromBranch) return [];

    let list = countersFromData?.data?.filter(
      (c) => getCounterBranchId(c) === String(fromBranch?.value)
    );

    return (list || []).map((c) => ({
      label: c.counterName,
      value: String(c._id),
    }));
  }, [fromBranch?.value, countersFromData]);

  const filteredToCounters = useMemo(() => {
    if (!toBranch) return [];

    let list = allTenantCounters.filter(
      (c) => getCounterBranchId(c) === String(toBranch.value)
    );

    if (String(toBranch.value) === String(fromBranch?.value || "")) {
      list = list.filter((c) => String(c._id) !== String(fromCounter?.value || ""));
    }

    return list.map((c) => ({
      label: c.counterName,
      value: String(c._id),
    }));
  }, [toBranch, allTenantCounters, fromBranch?.value, fromCounter?.value]);

  /* =========================
      RESET LOGIC
  ========================= */
  useEffect(() => {
    if (isStaff) return;
    setFromCounter(null);
    setFromEmployee(null);
  }, [fromBranch, isStaff]);

  useEffect(() => {
    setToCounter(null);
    setToEmployee(null);
  }, [toBranch]);

  useEffect(() => {
    if (isStaff) return;
    setFromEmployee(null);
  }, [fromCounter, isStaff]);

  useEffect(() => {
    setToEmployee(null);
  }, [toCounter]);

  /* =========================
      EMPLOYEE OPTIONS
  ========================= */
  const fromEmployeeOptions = useMemo(() => {
    if (isStaff) {
      return myEmployeeId ? [{ label: myEmployeeName, value: String(myEmployeeId) }] : [];
    }

    const rows = fromEmployeesResp?.data || [];
    return rows.map((e) => ({
      label:
        `${e.firstName || ""} ${e.lastName || ""}`.trim() ||
        e.officialEmail ||
        e.email ||
        "Employee",
      value: String(e._id),
    }));
  }, [isStaff, myEmployeeId, myEmployeeName, fromEmployeesResp]);

  // same as labelled transfer page
  const toEmployeeOptions = useMemo(() => {
    const rows = toEmployeesResp?.data || [];
    return rows.map((e) => ({
      label:
        `${e.firstName || ""} ${e.lastName || ""}`.trim() ||
        e.officialEmail ||
        e.email ||
        "Employee",
      value: String(e._id),
    }));
  }, [toEmployeesResp]);

  /* =========================
      STAFF INITIAL DEFAULTS
  ========================= */
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    if (!tenantId) return;
    if (!fromBranches.length) return;

    if (isStaff) {
      if (!fromEmployee && myEmployeeId) {
        setFromEmployee({ label: myEmployeeName, value: String(myEmployeeId) });
      }

      const preferredBranchId = String(emp?.branch || allowedBranchIds?.[0] || "");
      const bOpt =
        fromBranches.find((b) => String(b.value) === preferredBranchId) || fromBranches[0] || null;

      if (bOpt && !fromBranch) setFromBranch(bOpt);
      if (!toBranch && bOpt) setToBranch(bOpt);

      didInitRef.current = true;
    } else {
      didInitRef.current = true;
    }
  }, [
    tenantId,
    isStaff,
    fromBranches,
    fromBranch,
    toBranch,
    fromEmployee,
    myEmployeeId,
    myEmployeeName,
    emp?.branch,
    allowedBranchIds,
  ]);

  useEffect(() => {
    if (!isStaff) return;
    if (!fromBranch?.value) return;
    if (fromCounter?.value) return;

    const preferredCounterId = String(emp?.counter || allowedCounterIds?.[0] || "");
    const cOpt =
      filteredFromCounters.find((c) => String(c.value) === preferredCounterId) ||
      filteredFromCounters[0] ||
      null;

    if (cOpt) setFromCounter(cOpt);
  }, [
    isStaff,
    fromBranch?.value,
    filteredFromCounters,
    fromCounter?.value,
    emp?.counter,
    allowedCounterIds,
  ]);

  useEffect(() => {
    if (!isStaff) return;
    if (!toBranch?.value) return;
    if (toCounter?.value) return;

    const first = filteredToCounters[0] || null;
    if (first) setToCounter(first);
  }, [isStaff, toBranch?.value, filteredToCounters, toCounter?.value]);

  // same missing sync effect from labelled page
  const prevFromBranchRef = useRef(null);
  useEffect(() => {
    const prev = prevFromBranchRef.current;
    const curr = fromBranch;

    if (!prev && curr) {
      if (!toBranch) setToBranch(curr);
      prevFromBranchRef.current = curr;
      return;
    }

    if (prev?.value && curr?.value && String(prev.value) !== String(curr.value)) {
      if (!toBranch || String(toBranch.value) === String(prev.value)) {
        setToBranch(curr);
      }
    }

    prevFromBranchRef.current = curr;
  }, [fromBranch, toBranch]);

  /* =========================
      MODE LOGIC
  ========================= */
  useEffect(() => {
    if (transferMode !== "counter") return;
    if (!fromBranch) return;
    if (!toBranch || String(toBranch.value) !== String(fromBranch.value)) {
      setToBranch(fromBranch);
    }
  }, [transferMode, fromBranch, toBranch]);

  useEffect(() => {
    setToCounter(null);
    setToEmployee(null);
  }, [transferMode]);

  /* =========================
      BUCKETS QUERY
  ========================= */
  const shouldFetchBuckets = !!(fromBranch?.value && fromCounter?.value);

  const queryArgs = useMemo(
    () => ({
      tenantId,
      page,
      limit,
      search: debouncedSearch,
      branchId: fromBranch?.value || "",
      counterId: fromCounter?.value || "",
      isActive: filters.onlyActive ? "true" : "false",
    }),
    [
      tenantId,
      page,
      limit,
      debouncedSearch,
      fromBranch?.value,
      fromCounter?.value,
      filters.onlyActive,
    ]
  );

  const { data: bucketsResp, isFetching, refetch } = useGetAllUnlabelledBucketsQuery(queryArgs, {
    skip: !shouldFetchBuckets,
  });

  useEffect(() => {
    if (shouldFetchBuckets) refetch();
  }, [shouldFetchBuckets, fromBranch?.value, fromCounter?.value, refetch]);

  const pickBucketList = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    if (Array.isArray(resp)) return resp;
    return [];
  };

  const allBuckets = pickBucketList(bucketsResp);

  const buckets = useMemo(() => {
    if (filters.showZero) return allBuckets;
    return allBuckets.filter((b) => {
      const bal = balance(b);
      return bal.qty > 0 || bal.netWt > 0 || bal.grossWt > 0 || bal.totalWt > 0;
    });
  }, [allBuckets, filters.showZero]);

  const totalPages =
    bucketsResp?.pagination?.totalPages ||
    bucketsResp?.data?.pagination?.totalPages ||
    bucketsResp?.pages ||
    1;

  const availableBuckets = useMemo(
    () => buckets.filter((b) => !selectedBuckets.some((s) => sameId(s._id, b._id))),
    [buckets, selectedBuckets]
  );

  const [createTransfer, { isLoading }] = useCreateUnlabelledTransferMutation();

  /* =========================
      CHECKBOX / MOVE
  ========================= */
  const rightBucketRef = useRef(null);
  const leftBucketRef = useRef(null);

  const moveSelectedLeftToRight = () => {
    const selected = availableBuckets
      .filter((b) => checkedLeft.includes(String(b._id)))
      .map(withDefaultTransfer);

    if (!selected.length) return;

    setSelectedBuckets((p) => [...p, ...selected]);
    setCheckedLeft([]);

    if (!isSmUp) {
      setTimeout(() => {
        rightBucketRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const moveSelectedRightToLeft = () => {
    if (!checkedRight.length) return;
    setSelectedBuckets((p) => p.filter((b) => !checkedRight.includes(String(b._id))));
    setCheckedRight([]);

    if (!isSmUp) {
      setTimeout(() => {
        leftBucketRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const toggleSelectAllLeft = () => {
    const allIds = availableBuckets.map((b) => String(b._id));
    setCheckedLeft(allIds.length === checkedLeft.length ? [] : allIds);
  };

  const toggleSelectAllRight = () => {
    const allIds = selectedBuckets.map((b) => String(b._id));
    setCheckedRight(allIds.length === checkedRight.length ? [] : allIds);
  };

  /* =========================
      EDIT TRANSFER AMOUNTS
  ========================= */
 const setTransferField = (bucketId, field, value) => {
  setSelectedBuckets((prev) =>
    prev.map((b) => {
      if (!sameId(b._id, bucketId)) return b;

      const updatedTransfer = {
        ...(b._transfer || {}),
        [field]: value,
      };

      if (["grossWt", "stoneWt", "diamondWt"].includes(field)) {
        updatedTransfer.netWt = calcNetWt(
          updatedTransfer.grossWt,
          updatedTransfer.stoneWt,
          updatedTransfer.diamondWt
        );
      }

      return {
        ...b,
        _transfer: updatedTransfer,
      };
    })
  );
};

 const rightTotals = useMemo(() => {
  let qty = 0;
  let grossWt = 0;
  let stoneWt = 0;
  let diamondWt = 0;
  let netWt = 0;

  selectedBuckets.forEach((b) => {
    qty += n(b?._transfer?.qty);
    grossWt += n(b?._transfer?.grossWt);
    stoneWt += n(b?._transfer?.stoneWt);
    diamondWt += n(b?._transfer?.diamondWt);
    netWt += n(b?._transfer?.netWt);
  });

  return {
    qty,
    grossWt: r3(grossWt),
    stoneWt: r3(stoneWt),
    diamondWt: r3(diamondWt),
    netWt: r3(netWt),
  };
}, [selectedBuckets]);

  /* =========================
      SUBMIT
  ========================= */
 const validateBuckets = () => {
  for (const b of selectedBuckets) {
    const bal = balance(b);

    const tQty = n(b?._transfer?.qty);
    const tGross = r3(b?._transfer?.grossWt);
    const tStone = r3(b?._transfer?.stoneWt);
    const tDiamond = r3(b?._transfer?.diamondWt);
    const tNet = r3(b?._transfer?.netWt);

    if (tQty <= 0 && tGross <= 0 && tStone <= 0 && tDiamond <= 0 && tNet <= 0) {
      return `Enter transfer values for: ${comboTitle(b)}`;
    }

    if (tQty > bal.qty) return `Transfer Qty exceeds available for: ${comboTitle(b)}`;
    if (tGross > bal.grossWt) return `Transfer GrossWt exceeds available for: ${comboTitle(b)}`;
    if (tStone > bal.stoneWt) return `Transfer StoneWt exceeds available for: ${comboTitle(b)}`;
    if (tDiamond > bal.diamondWt) return `Transfer DiamondWt exceeds available for: ${comboTitle(b)}`;
    if (tNet > bal.netWt) return `Transfer NetWt exceeds available for: ${comboTitle(b)}`;

    if (tQty < 0 || tGross < 0 || tStone < 0 || tDiamond < 0 || tNet < 0) {
      return `Invalid negative transfer for: ${comboTitle(b)}`;
    }
  }

  return "";
};

  const handleSubmit = async () => {
    const required = [fromBranch, fromCounter, fromEmployee, toBranch, toCounter, toEmployee];
    if (required.some((x) => !x)) {
      toast.error("⚠ Please fill all required fields before creating transfer");
      return;
    }

    if (selectedBuckets.length === 0) {
      toast.error("Please select at least one bucket to transfer");
      return;
    }

    const errMsg = validateBuckets();
    if (errMsg) {
      toast.error(errMsg);
      return;
    }

    const payload = {
      tenantId,
      transferType: transferMode === "branch" ? "branch" : "counter",

      fromBranchId: fromBranch.value,
      fromBranchName: fromBranch.label,
      toBranchId: toBranch.value,
      toBranchName: toBranch.label,

      fromCounterId: fromCounter.value,
      fromCounterName: fromCounter.label,
      toCounterId: toCounter.value,
      toCounterName: toCounter.label,

      fromUserId: fromEmployee.value,
      fromUserName: fromEmployee.label,
      toUserId: toEmployee.value,
      toUserName: toEmployee.label,

      items: selectedBuckets.map((b) => ({
        bucketId: b._id,

        categoryId: b.categoryId,
        productId: b.productId,
        designId: b.designId,
        purityId: b.purityId,

        categoryName: b.categoryName || "",
        productName: b.productName || "",
        designName: b.designName || "",
        purityName: b.purityName || "",

        available: {
          qty: n(b?.balance?.qty),
          netWt: r3(b?.balance?.netWt),
          grossWt: r3(b?.balance?.grossWt),
          totalWt: r3(b?.balance?.grossWt),
          stoneWt: r3(b?.balance?.stoneWt),
          diamondWt: r3(b?.balance?.diamondWt),
        },

        transfer: {
          qty: n(b?._transfer?.qty),
          netWt: r3(b?._transfer?.netWt),
          grossWt: r3(b?._transfer?.grossWt),
          totalWt: r3(b?._transfer?.grossWt),
          stoneWt: r3(b?._transfer?.stoneWt),
          diamondWt: r3(b?._transfer?.diamondWt),
        },
      })),
    };
   console.log(payload)
    // return  
    try {
      await createTransfer(payload).unwrap();
      toast.success("✅ Transfer created successfully");
      navigate("/unlabelled-transfers");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create transfer");
    }
  };

  const handleResetFilters = () => {
    setFilters({ showZero: false, onlyActive: true });
    setSearch("");
    setPage(1);
  };

  const disableFromTo = selectedBuckets.length > 0;
  const isFromEmployeeLocked = isStaff || disableFromTo;
  const isToBranchDisabled = disableFromTo || transferMode === "counter" || !fromBranch;
  const checkedTotal = checkedLeft.length + checkedRight.length;

  return (
    <div className="relative bg-white shadow-lg rounded-2xl border border-slate-200 flex flex-col overflow-hidden h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {isFetching && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/55">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading buckets…</span>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="shrink-0 bg-[#9481ce] text-white border-b border-white/10">
        <div className="px-3 sm:px-4 pt-2 pb-2">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-8 w-8 sm:h-9 sm:w-9 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <FaArrowRightArrowLeft size={15} />
              </span>
              <div className="min-w-0">
                <h2 className="text-[14px] sm:text-[17px] font-semibold tracking-tight truncate">
                  Unlabelled Transfer (Buckets)
                </h2>
                <p className="text-[10px] sm:text-[11px] text-violet-100/90 truncate">
                  Source → Destination • Move bucket-wise unlabelled stock
                </p>
              </div>

              {isFetching && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 shrink-0">
                  <Spinner size={10} /> refreshing…
                </span>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <div className="inline-flex items-center gap-1 p-[2px] rounded-full bg-white/15 border border-white/15">
                <button
                  type="button"
                  onClick={() => setTransferMode("counter")}
                  className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                    transferMode === "counter"
                      ? "bg-white text-[#5a46c8] shadow-sm"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  Counter ↔ Counter
                </button>
                <button
                  type="button"
                  onClick={() => setTransferMode("branch")}
                  className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                    transferMode === "branch"
                      ? "bg-white text-[#5a46c8] shadow-sm"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  Branch ↔ Branch
                </button>
              </div>

              <div className="hidden lg:flex items-center gap-2 ml-2">
                <span className={chipCls}>
                  Selected: <span className="font-semibold">{selectedBuckets.length}</span>
                </span>
                <span className={chipCls}>
                  Checked: <span className="font-semibold">{checkedTotal}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="sm:hidden mt-2">
            <div className="inline-flex items-center gap-1 p-[2px] rounded-full bg-white/15 border border-white/15 w-full">
              <button
                type="button"
                onClick={() => setTransferMode("counter")}
                className={`flex-1 px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                  transferMode === "counter"
                    ? "bg-white text-[#5a46c8] shadow-sm"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                Counter ↔ Counter
              </button>
              <button
                type="button"
                onClick={() => setTransferMode("branch")}
                className={`flex-1 px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                  transferMode === "branch"
                    ? "bg-white text-[#5a46c8] shadow-sm"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                Branch ↔ Branch
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 min-h-0 overflow-auto md:overflow-hidden">
        {/* FROM / TO */}
        <div className="shrink-0 p-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                  From (Source)
                </p>
                <span className="text-[10px] text-slate-500">
                  {disableFromTo ? "Locked (buckets selected)" : isStaff ? "Staff mode" : "Editable"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Branch *</label>
                  <Select
                    styles={selectStyles}
                    options={fromBranches}
                    value={fromBranch}
                    onChange={(v) => {
                      setFromBranch(v);
                      setPage(1);
                      setSelectedBuckets([]);
                      setCheckedLeft([]);
                      setCheckedRight([]);
                    }}
                    isDisabled={disableFromTo}
                    placeholder="Select branch"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Counter *</label>
                  <Select
                    styles={selectStyles}
                    options={filteredFromCounters}
                    value={fromCounter}
                    onChange={(v) => {
                      setFromCounter(v);
                      setPage(1);
                      setSelectedBuckets([]);
                      setCheckedLeft([]);
                      setCheckedRight([]);
                    }}
                    placeholder="Select counter"
                    isDisabled={!fromBranch || disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Employee *</label>
                  <Select
                    styles={selectStyles}
                    options={fromEmployeeOptions}
                    value={fromEmployee}
                    onChange={setFromEmployee}
                    isDisabled={isFromEmployeeLocked}
                    placeholder="Select employee"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                  To (Destination)
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Branch *</label>
                  <Select
                    styles={selectStyles}
                    options={toBranches}
                    value={toBranch}
                    onChange={setToBranch}
                    isDisabled={isToBranchDisabled}
                    placeholder={transferMode === "counter" ? "Same as source" : "Select branch"}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Counter *</label>
                  <Select
                    styles={selectStyles}
                    options={filteredToCounters}
                    value={toCounter}
                    onChange={setToCounter}
                    placeholder="Select counter"
                    isDisabled={!toBranch || disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Employee *</label>
                  <Select
                    styles={selectStyles}
                    options={toEmployeeOptions}
                    value={toEmployee}
                    onChange={setToEmployee}
                    placeholder="Select employee"
                    isDisabled={disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BUCKETS */}
        <div className="flex-1 min-h-0 flex flex-col md:grid md:grid-cols-2 gap-3 p-2 pt-0">
          {/* LEFT */}
          <div
            ref={leftBucketRef}
            className="border border-slate-200 rounded-2xl bg-white flex flex-col flex-1 min-h-0 shadow-[0_1px_0_rgba(15,23,42,0.03)]"
          >
            <div className="shrink-0 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] px-4 py-2 flex justify-between items-center border-b border-slate-200 rounded-t-2xl">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shrink-0 shadow-sm">
                  <FiBox size={15} />
                </span>
                <div className="leading-tight min-w-0">
                  <p className="font-semibold text-[13px] truncate">Available Buckets</p>
                  <p className="text-[11px] text-[#4b3d91]/70 truncate">
                    {shouldFetchBuckets
                      ? `Showing ${availableBuckets.length} buckets`
                      : "Select branch & counter"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => shouldFetchBuckets && refetch()}
                  disabled={!shouldFetchBuckets}
                  className="inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                  title="Refresh"
                >
                  <FiRefreshCcw size={13} /> <span className="hidden sm:inline">Refresh</span>
                </button>

                <button
                  onClick={toggleSelectAllLeft}
                  disabled={!availableBuckets.length}
                  className="text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                  title="Select all available"
                >
                  {checkedLeft.length === availableBuckets.length && availableBuckets.length > 0
                    ? "Unselect"
                    : "Select all"}
                </button>
              </div>
            </div>

            <div className="shrink-0 px-3 sm:px-4 py-2 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <div className="flex items-center border border-slate-200 rounded-2xl px-3 py-2 bg-white w-full">
                  <FiSearch className="text-slate-400 mr-2" size={14} />
                  <input
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search category / product / design / purity…"
                    className="outline-none text-[12px] w-full bg-transparent"
                    disabled={!shouldFetchBuckets}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowFilterModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-[12px] font-semibold"
                  title="Filters"
                >
                  <FiFilter />
                </button>

                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-2 px-3 py-2 text-[12px] rounded-full border border-slate-200 bg-white hover:bg-slate-100"
                  >
                    <FiRefreshCcw size={14} /> Reset
                  </button>

                  <button
                    onClick={moveSelectedLeftToRight}
                    disabled={!checkedLeft.length}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#6b59c6] text-white text-[12px] font-semibold shadow-sm hover:bg-[#5a48a8] disabled:opacity-50"
                  >
                    Move <FiArrowRight />
                  </button>
                </div>
              </div>

              <div className="sm:hidden mt-2">
                <button
                  onClick={moveSelectedLeftToRight}
                  disabled={!checkedLeft.length}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl bg-[#6b59c6] text-white text-[12px] font-semibold shadow-sm hover:bg-[#5a48a8] disabled:opacity-50"
                >
                  Move Selected <FiArrowRight />
                </button>
              </div>
            </div>

            <div className={tableScrollCls}>
              <table className="w-full table-auto">
                <thead className="bg-[#f2efff] text-[#4b3d91] sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className={`${thCls} w-11 text-center`}>☑</th>
                    <th className={thCls}>Bucket</th>
                    <th className={`${thCls} text-right w-24`}>G Wt</th>
                    <th className={`${thCls} text-right w-24`}>Stn Wt</th>
                    <th className={`${thCls} text-right w-24`}>Dia Wt(ct)</th>
                    <th className={`${thCls} text-right w-28`}>N Wt</th>
                    <th className={`${thCls} text-right w-24`}>Qty</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700">
                  {!shouldFetchBuckets ? (
                    <tr>
                      <td colSpan={4} className="text-center py-10 text-sm text-slate-500">
                        Select branch & counter to load buckets.
                      </td>
                    </tr>
                  ) : isFetching ? (
                    <tr>
                      <td colSpan={4} className="text-center py-10 text-sm text-slate-500">
                        Loading buckets…
                      </td>
                    </tr>
                  ) : availableBuckets.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-10 text-sm text-slate-400">
                        No buckets found with current filters.
                      </td>
                    </tr>
                  ) : (
                    availableBuckets.map((b) => {
                      const bal = balance(b);
                      return (
                        <tr
                          key={b._id}
                          className="border-t border-slate-100 hover:bg-[#f6f2ff] transition"
                        >
                          <td className={`${tdCls} text-center`}>
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#6b59c6]"
                              checked={checkedLeft.includes(String(b._id))}
                              onChange={() =>
                                setCheckedLeft((p) =>
                                  p.includes(String(b._id))
                                    ? p.filter((x) => x !== String(b._id))
                                    : [...p, String(b._id)]
                                )
                              }
                            />
                          </td>

                          <td className={`${tdCls} font-semibold text-[#6b59c6]`}>
                            <div className="flex flex-col leading-tight min-w-0">
                              <span className="truncate">{comboTitle(b)}</span>
                              <span className="text-[10px] font-medium text-slate-500 truncate">
                                {b.branchName ? `${b.branchName}` : ""}
                                {b.counterName ? ` • ${b.counterName}` : ""}
                              </span>
                            </div>
                          </td>

                          <td className={`${tdCls} text-right whitespace-nowrap`}>
                            {bal?.grossWt?.toFixed(3)}
                          </td>
                          <td className={`${tdCls} text-right whitespace-nowrap`}>
                            {bal?.stoneWt?.toFixed(3)}
                          </td>
                          <td className={`${tdCls} text-right whitespace-nowrap`}>
                            {bal?.diamondWt?.toFixed(3)}
                          </td>
                          <td className={`${tdCls} text-right whitespace-nowrap`}>
                            {bal?.netWt?.toFixed(3)}
                          </td>
                          <td className={`${tdCls} text-right`}>{bal.qty}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="shrink-0 border-t border-slate-200 px-4 py-2 flex items-center justify-between bg-slate-50 rounded-b-2xl">
              {shouldFetchBuckets && totalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-[12px] rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span className="text-[12px] text-slate-600">
                    Page <span className="font-semibold">{page}</span> /{" "}
                    <span className="font-semibold">{totalPages}</span>
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-[12px] rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              ) : (
                <span className="text-[12px] text-slate-500">—</span>
              )}

              <span className="text-[12px] text-slate-600">
                Checked: <span className="font-semibold">{checkedLeft.length}</span>
              </span>
            </div>
          </div>

          {/* RIGHT */}
          <div
            ref={rightBucketRef}
            className="border border-slate-200 rounded-2xl overflow-hidden bg-white flex flex-col flex-1 min-h-0 shadow-[0_1px_0_rgba(15,23,42,0.03)]"
          >
            <div className="shrink-0 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] px-4 py-2 flex justify-between items-center border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shrink-0 shadow-sm">
                  <FiSend size={15} />
                </span>
                <div className="leading-tight min-w-0">
                  <p className="font-semibold text-[13px] truncate">Selected Buckets</p>
                  <p className="text-[11px] text-[#4b3d91]/70 truncate">
  Buckets: <span className="font-semibold">{selectedBuckets.length}</span> • Qty:{" "}
  <span className="font-semibold">{rightTotals.qty}</span> • G Wt:{" "}
  <span className="font-semibold">{rightTotals.grossWt.toFixed(3)}</span> • Stn Wt:{" "}
  <span className="font-semibold">{rightTotals.stoneWt.toFixed(3)}</span> • Dia Wt:{" "}
  <span className="font-semibold">{rightTotals.diamondWt.toFixed(3)}</span> • N Wt:{" "}
  <span className="font-semibold">{rightTotals.netWt.toFixed(3)}</span>
</p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={toggleSelectAllRight}
                  disabled={!selectedBuckets.length}
                  className="text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                >
                  {checkedRight.length === selectedBuckets.length && selectedBuckets.length > 0
                    ? "Unselect"
                    : "Select all"}
                </button>

                <button
                  onClick={moveSelectedRightToLeft}
                  disabled={!checkedRight.length}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-500 text-white text-[12px] font-semibold shadow-sm hover:bg-rose-600 disabled:opacity-50"
                >
                  <FiArrowLeft /> Remove
                </button>
              </div>
            </div>

            <div className="min-h-0 overflow-auto h-[340px] sm:h-[380px] md:h-[220px]">
              <table className="w-full table-auto">
               <thead className="bg-[#f2efff] text-[#4b3d91] sticky top-0 z-10 border-b border-slate-200">
  <tr>
    <th className={`${thCls} w-11 text-center`}>☑</th>
    <th className={thCls}>Bucket</th>
    <th className={`${thCls} text-right w-24`}>Qty</th>
    <th className={`${thCls} text-right w-24`}>G Wt</th>
    <th className={`${thCls} text-right w-24`}>Stn Wt</th>
    <th className={`${thCls} text-right w-24`}>Dia Wt</th>
    <th className={`${thCls} text-right w-24`}>N Wt</th>
  </tr>
</thead>

                <tbody className="text-gray-700">
                  {selectedBuckets.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-sm text-slate-400">
                        No buckets selected yet. Select and move →
                      </td>
                    </tr>
                  ) : (
                   selectedBuckets.map((b) => {
  const bal = balance(b);

  const tQty = b?._transfer?.qty ?? "";
  const tGross = b?._transfer?.grossWt ?? "";
  const tStone = b?._transfer?.stoneWt ?? "";
  const tDiamond = b?._transfer?.diamondWt ?? "";
  const tNet = b?._transfer?.netWt ?? "";

  const qtyOver = n(tQty) > bal.qty;
  const grossOver = n(tGross) > bal.grossWt;
  const stoneOver = n(tStone) > bal.stoneWt;
  const diamondOver = n(tDiamond) > bal.diamondWt;
  const netOver = n(tNet) > bal.netWt;

  return (
    <tr
      key={b._id}
      className="border-t border-slate-100 hover:bg-rose-50/60 transition"
    >
      <td className={`${tdCls} text-center`}>
        <input
          type="checkbox"
          className="h-4 w-4 accent-rose-500"
          checked={checkedRight.includes(String(b._id))}
          onChange={() =>
            setCheckedRight((p) =>
              p.includes(String(b._id))
                ? p.filter((x) => x !== String(b._id))
                : [...p, String(b._id)]
            )
          }
        />
      </td>

      <td className={`${tdCls} font-semibold text-[#6b59c6]`}>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="truncate">{comboTitle(b)}</span>
          <span className="text-[10px] font-medium text-slate-500 truncate">
            Avail: Qty {bal.qty} • G {bal.grossWt.toFixed(3)} • S {bal.stoneWt.toFixed(3)} • D {bal.diamondWt.toFixed(3)} • N {bal.netWt.toFixed(3)}
          </span>
        </div>
      </td>

      <td className={`${tdCls} text-right`}>
        <input
          value={tQty}
          onChange={(e) => setTransferField(b._id, "qty", e.target.value)}
          className={`w-20 text-right px-2 py-1 rounded-xl border text-[12px] outline-none ${
            qtyOver ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"
          }`}
          placeholder="0"
          inputMode="numeric"
        />
      </td>

      <td className={`${tdCls} text-right`}>
        <input
          value={tGross}
          onChange={(e) => setTransferField(b._id, "grossWt", e.target.value)}
          className={`w-24 text-right px-2 py-1 rounded-xl border text-[12px] outline-none ${
            grossOver ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"
          }`}
          placeholder="0.000"
          inputMode="decimal"
        />
      </td>

      <td className={`${tdCls} text-right`}>
        <input
          value={tStone}
          onChange={(e) => setTransferField(b._id, "stoneWt", e.target.value)}
          className={`w-24 text-right px-2 py-1 rounded-xl border text-[12px] outline-none ${
            stoneOver ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"
          }`}
          placeholder="0.000"
          inputMode="decimal"
        />
      </td>

      <td className={`${tdCls} text-right`}>
        <input
          value={tDiamond}
          onChange={(e) => setTransferField(b._id, "diamondWt", e.target.value)}
          className={`w-24 text-right px-2 py-1 rounded-xl border text-[12px] outline-none ${
            diamondOver ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"
          }`}
          placeholder="0.000"
          inputMode="decimal"
        />
      </td>

      <td className={`${tdCls} text-right`}>
        <input
          value={tNet}
          disabled
          readOnly
          // onChange={(e) => setTransferField(b._id, "netWt", e.target.value)}
          className={`w-24 text-right px-2 py-1 rounded-xl border text-[12px] outline-none ${
            netOver ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"
          }`}
          placeholder="0.000"
          inputMode="decimal"
        />
      </td>
    </tr>
  );
})
                  )}
                </tbody>
              </table>
            </div>

            <div className="shrink-0 border-t border-slate-200 px-4 py-2 flex items-center justify-between bg-slate-50">
              <span className="text-[12px] text-slate-600">
                Checked: <span className="font-semibold">{checkedRight.length}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop/Tablet bottom actions */}
      <div className="hidden sm:flex shrink-0 border-t border-slate-200 bg-white/90 backdrop-blur px-4 py-2">
        <div className="w-full flex items-center justify-end gap-2">
          <button
            onClick={() => navigate("/unlabelled-transfers")}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#9481ce] px-5 py-2 text-[12px] font-semibold text-white shadow-sm hover:bg-[#5a48a8] disabled:opacity-60"
          >
            {isLoading ? "Creating..." : "Create Transfer"}
          </button>
        </div>
      </div>

      {/* Mobile sticky actions */}
      <div className="sm:hidden shrink-0 border-t border-slate-200 bg-white/95 backdrop-blur px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/unlabelled-transfers")}
            className="w-[40%] inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700"
          >
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-[60%] inline-flex items-center justify-center gap-2 rounded-2xl bg-[#6b59c6] px-3 py-2 text-[12px] font-semibold text-white shadow-sm disabled:opacity-60"
          >
            {isLoading ? "Creating..." : "Create Transfer"}
          </button>
        </div>
      </div>

      {/* FILTER MODAL */}
      {showFilterModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 bg-black/40"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowFilterModal(false);
          }}
        >
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] flex items-center justify-between border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shadow-sm">
                  <FiFilter size={15} />
                </span>
                <div className="leading-tight">
                  <p className="font-semibold text-[13px]">Filters</p>
                  <p className="text-[11px] text-[#4b3d91]/70">Bucket list filters</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowFilterModal(false)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50"
                title="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-[12px] text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#6b59c6]"
                    checked={!!filters.onlyActive}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, onlyActive: e.target.checked }))
                    }
                  />
                  Only Active buckets
                </label>

                <label className="flex items-center gap-2 text-[12px] text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#6b59c6]"
                    checked={!!filters.showZero}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, showZero: e.target.checked }))
                    }
                  />
                  Show zero balance buckets
                </label>
              </div>

              <div className="mt-3 text-[11px] text-slate-500">
                Note: Category/Product/Design/Purity filters API me later add kar sakte — UI structure ready hai.
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-2 px-4 py-2 text-[12px] rounded-2xl border border-slate-200 bg-white hover:bg-slate-100"
              >
                <FiRefreshCcw size={14} /> Reset
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (shouldFetchBuckets) refetch();
                    setShowFilterModal(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[12px] rounded-2xl border border-slate-200 bg-white hover:bg-slate-100"
                >
                  <FiRefreshCcw size={14} /> Refresh
                </button>

                <button
                  type="button"
                  onClick={() => setShowFilterModal(false)}
                  className="inline-flex items-center gap-2 px-5 py-2 text-[12px] rounded-2xl bg-[#6b59c6] text-white font-semibold hover:bg-[#5a48a8]"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlabelledTransferCreateDualBucket;